"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var ksys_app_template_1 = require("@kurtosys/ksys-app-template");
var DEFAULT_COUNT = 5;
var DEFAULT_INTERVALS = [
    0.01, 0.02, 0.05,
    0.1, 0.2, 0.5,
    1, 2, 5,
    10, 20, 50,
    100, 200, 500,
    1000, 2000, 5000,
    10000, 20000, 50000,
    100000, 200000, 500000,
    1000000, 2000000, 5000000,
    10000000, 20000000, 50000000,
    100000000, 200000000, 500000000
];
var DEFAULT_TOP_BAR_LABELS = false;
var DEFAULT_PRECISION = 0;
var DEFAULT_TICK_PADDING_THRESHOLD_PERCENTAGE = 35;
var TickHandler = /** @class */ (function () {
    function TickHandler(options) {
        this.options = this.orchestrateOptions(options);
    }
    TickHandler.prototype.orchestrateOptions = function (options) {
        return ksys_app_template_1.utils.object.deepMergeObjects(this.defaultOptions, options);
    };
    Object.defineProperty(TickHandler.prototype, "defaultOptions", {
        get: function () {
            var response = {
                count: DEFAULT_COUNT,
                intervals: DEFAULT_INTERVALS,
                usingTopBarLabels: DEFAULT_TOP_BAR_LABELS,
                precision: DEFAULT_PRECISION,
                tickPaddingThresholdPercentage: DEFAULT_TICK_PADDING_THRESHOLD_PERCENTAGE,
            };
            return response;
        },
        enumerable: true,
        configurable: true
    });
    TickHandler.prototype.getTicksForSeries = function (data, seriesKeys, point, axis, center, persistQuadrants) {
        if (data && seriesKeys) {
            var getKeyFns = this.getFunctionsForSeriesKeys(seriesKeys);
            var domain = this.getDomain(data, getKeyFns);
            if (domain) {
                var min = domain.min, max = domain.max;
                var ticks = this.getTicksAroundPoint(ksys_app_template_1.utils.typeChecks.isUndefined(point) ? 1 : point, max, min, ksys_app_template_1.utils.typeChecks.isUndefined(center) ? false : center, ksys_app_template_1.utils.typeChecks.isUndefined(persistQuadrants) ? false : persistQuadrants);
                return ticks;
            }
        }
        return;
    };
    TickHandler.prototype.getTicksForLogScale = function (point, actualMax) {
        var current = point;
        var ticks = [current];
        while (current <= actualMax) {
            current = current * 2;
            ticks.push(current);
        }
        return ticks;
    };
    TickHandler.prototype.getTicksAroundPoint = function (point, max, min, center, persistQuadrants) {
        var _a = this.options, _b = _a.count, count = _b === void 0 ? DEFAULT_COUNT : _b, _c = _a.intervals, intervals = _c === void 0 ? DEFAULT_INTERVALS : _c;
        var actualMax = Math.max(max, point);
        var actualMin = Math.min(min, point);
        var gap = this.applyPrecision(actualMax - actualMin);
        var bestInterval = intervals.find(function (interval) { return interval >= gap / count; });
        var ticks = [];
        var elementProps = this.options.elementProps;
        ticks.push(point);
        if (elementProps && elementProps.scale === "log" && point > 0) {
            ticks = this.getTicksForLogScale(point, actualMax);
        }
        else {
            if (bestInterval) {
                var maxRunner = point;
                while (maxRunner < actualMax) {
                    maxRunner += bestInterval;
                    this.pushTick(ticks, maxRunner);
                }
                var minRunner = point;
                while (minRunner > actualMin) {
                    minRunner -= bestInterval;
                    this.pushTick(ticks, minRunner);
                }
                if (center) {
                    var pointsAfterPivot = ticks.reduce(function (arr, number) {
                        if (number > point) {
                            return arr += 1;
                        }
                        return arr;
                    }, 0);
                    var pointsBeforePivot = ticks.reduce(function (arr, number) {
                        if (number < point) {
                            return arr += 1;
                        }
                        return arr;
                    }, 0);
                    if (pointsAfterPivot > pointsBeforePivot) {
                        for (var i = 0; i <= pointsAfterPivot; i++) {
                            if (i > pointsBeforePivot) {
                                minRunner -= bestInterval;
                                this.pushTick(ticks, minRunner);
                            }
                        }
                    }
                    else if (pointsAfterPivot < pointsBeforePivot) {
                        for (var i = 0; i <= pointsBeforePivot; i++) {
                            if (i > pointsAfterPivot) {
                                maxRunner += bestInterval;
                                this.pushTick(ticks, minRunner);
                            }
                        }
                    }
                }
                if (this.options.usingTopBarLabels && this.options.tickPaddingThresholdPercentage) {
                    var maxTick = Math.max.apply(Math, ticks);
                    if (maxTick !== point) {
                        var maxDiff = (maxTick - actualMax) / bestInterval * 100;
                        if (Math.abs(maxDiff) < this.options.tickPaddingThresholdPercentage) {
                            this.pushTick(ticks, maxTick + bestInterval);
                        }
                    }
                    var minTick = Math.min.apply(Math, ticks);
                    if (minTick !== point) {
                        var minDiff = (minTick - actualMin) / bestInterval * 100;
                        if (Math.abs(minDiff) < this.options.tickPaddingThresholdPercentage) {
                            this.pushTick(ticks, minTick - bestInterval);
                        }
                    }
                }
            }
            if (persistQuadrants && bestInterval) {
                for (var i = Math.min.apply(Math, ticks); i >= 0; i = i - bestInterval) {
                    this.pushTick(ticks, i - bestInterval);
                }
                for (var i = Math.max.apply(Math, ticks); i <= 0; i = i + bestInterval) {
                    this.pushTick(ticks, i + bestInterval);
                }
            }
        }
        ticks = ksys_app_template_1.utils.collection.unique(ticks, function (e) { return e.toString(); });
        var sortedTicks = ticks.sort(function (a, b) { return a - b; });
        return sortedTicks;
    };
    TickHandler.prototype.applyPrecision = function (number) {
        return parseFloat(number.toFixed(this.options.precision));
    };
    TickHandler.prototype.pushTick = function (ticks, tick) {
        var floatPrecisionFix = this.applyPrecision(tick); // handling floating point precision
        ticks.push(floatPrecisionFix);
    };
    TickHandler.prototype.getDomain = function (data, getKeyFns) {
        var min = null;
        var max = null;
        for (var _i = 0, data_1 = data; _i < data_1.length; _i++) {
            var row = data_1[_i];
            if (row) {
                for (var _a = 0, getKeyFns_1 = getKeyFns; _a < getKeyFns_1.length; _a++) {
                    var getKeyFn = getKeyFns_1[_a];
                    if (getKeyFn) {
                        var value = getKeyFn(row);
                        if (!Number.isNaN(value) && !ksys_app_template_1.utils.typeChecks.isNullOrUndefined(value)) {
                            min = min === null ? value : Math.min(min, value);
                            max = max === null ? value : Math.max(max, value);
                        }
                    }
                }
            }
        }
        if (max === null) {
            max = 0;
        }
        if (min === null) {
            min = 0;
        }
        max = this.dataMaxFn(max);
        min = this.dataMinFn(min);
        return { max: max, min: min };
    };
    TickHandler.prototype.getFunctionsForSeriesKeys = function (series) {
        var response = [];
        var _loop_1 = function (serie) {
            response.push(function (row) { return row[serie]; });
        };
        for (var _i = 0, series_1 = series; _i < series_1.length; _i++) {
            var serie = series_1[_i];
            _loop_1(serie);
        }
        return response;
    };
    TickHandler.prototype.dataMinFn = function (dataMin) {
        return Math.floor(dataMin * 100) / 100;
    };
    TickHandler.prototype.dataMaxFn = function (dataMax) {
        return Math.ceil(dataMax * 100) / 100;
    };
    return TickHandler;
}());
exports.TickHandler = TickHandler;
